import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { ActiveWorkRun } from '@sr-test-wizard/shared';
import { api } from '../lib/api';
import { useAuth } from '../auth/AuthContext';
import { useWebSocket } from '../hooks/useWebSocket';
import { CustomSrDialog } from '../features/home/CustomSrDialog';
import { TexecPicker } from '../features/home/TexecPicker';
import { StatusIcon, type IconKind } from '../components/StatusIcon';
import { useActiveWorkStore } from '../state/activeWorkStore';
import {
  boardHasUpdatesSinceVisit,
  saveBoardRunSnapshot,
} from '../lib/boardVisit';
import { formatAppDate } from '../lib/localTime';

interface Chips {
  texec: { done: number; total: number; status: string };
  video: { analyzed: number; total: number; status: string };
  gameKb: { status: string };
  artifacts: { files: string; videos: string; images: string };
}

interface DashCard {
  srKey: string;
  srSummary: string;
  studioName: string;
  status: string;
  assignee: string | null;
  texecAssignees?: string[];
  texecKeys?: string[];
  created: string | null;
  runId: string | null;
  ownerEmail: string | null;
  closedAt: string | null;
  chips: Chips | null;
  bugs?: { inReview: number; todo: number };
  runs: Array<{ id: string; texecKey: string; gameName: string; chips: Chips }>;
}

interface DashboardData {
  columns: string[];
  assignees: string[];
  cards: DashCard[];
  generatedAt: string;
}

type ChipActivity = 'running' | 'paused' | 'done' | null;

const EMPTY_CHIPS: Chips = {
  texec: { done: 0, total: 0, status: 'idle' },
  video: { analyzed: 0, total: 0, status: 'idle' },
  gameKb: { status: 'idle' },
  artifacts: { files: 'missing', videos: 'missing', images: 'missing' },
};

function Chip({
  label,
  activity,
  tone,
}: {
  label: string;
  activity?: ChipActivity;
  tone?: string;
}) {
  const iconStatus: IconKind | null =
    activity === 'running' ? 'in_progress' : activity === 'paused' ? 'paused' : null;
  const resolvedTone =
    tone ||
    (activity === 'running' ? 'info' : activity === 'paused' ? 'warn' : activity === 'done' ? 'good' : '');
  return (
    <span className={`badge kanban-chip ${resolvedTone}`.trim()}>
      {iconStatus && <StatusIcon status={iconStatus} size={12} />}
      <span>{label}</span>
    </span>
  );
}

function chipsOf(c: DashCard): Chips {
  return c.chips ?? EMPTY_CHIPS;
}

function activityFromStatus(status: string, busy: boolean): ChipActivity {
  if (busy || status === 'running') return 'running';
  if (status === 'done' || status === 'ready') return 'done';
  if (status === 'partial') return 'paused';
  return null;
}

/** Pick the busiest live activity among all local runs under this SR card. */
function pickActiveWorkForCard(
  card: DashCard,
  byRunId: Map<string, ActiveWorkRun>,
): ActiveWorkRun | null {
  const ids = [
    ...(card.runId ? [card.runId] : []),
    ...card.runs.map((r) => r.id),
  ];
  let best: ActiveWorkRun | null = null;
  let bestScore = -1;
  for (const id of ids) {
    const aw = byRunId.get(id);
    if (!aw) continue;
    const f = aw.flags;
    const score =
      (f.autoWorkflowBusy ? 8 : 0) +
      (f.texecAnalyzing ? 4 : 0) +
      (f.imagesBusy ? 2 : 0) +
      (f.videoBusy || f.driveVideoBusy ? 2 : 0) +
      (f.pullSourceBusy ? 1 : 0) +
      aw.jobs.length;
    if (score > bestScore) {
      best = aw;
      bestScore = score;
    }
  }
  return best;
}

function autoStepTargets(step: string | null | undefined): {
  images: boolean;
  video: boolean;
  docs: boolean;
  texec: boolean;
  source: boolean;
} {
  const s = step || '';
  return {
    images: s.includes('images') || s.includes('download-images') || s.includes('analyze-images'),
    video: s.startsWith('video'),
    docs: s.startsWith('game-kb'),
    texec: s.startsWith('load-tests') || s.startsWith('analyze-tests'),
    source: s.startsWith('pull-source'),
  };
}

function persistCardSnapshot(card: DashCard) {
  if (!card.runId) return;
  const chips = chipsOf(card);
  saveBoardRunSnapshot(card.runId, {
    texecDone: chips.texec.done,
    texecTotal: chips.texec.total,
    videoAnalyzed: chips.video.analyzed,
    videoTotal: chips.video.total,
    docsReady: chips.gameKb.status === 'ready',
    bugsInReview: card.bugs?.inReview ?? 0,
  });
}

function CardBody({
  card,
  activeWork,
  onOpen,
}: {
  card: DashCard;
  activeWork: ActiveWorkRun | null;
  onOpen: () => void;
}) {
  const chips = chipsOf(card);
  const started = !!card.runId;
  const stepTargets = autoStepTargets(activeWork?.autoWorkflowStep);
  const autoBusy = !!activeWork?.flags.autoWorkflowBusy;

  const texecBusy =
    !!activeWork?.flags.texecAnalyzing || (autoBusy && stepTargets.texec);
  const videoBusy = !!(
    activeWork?.flags.videoBusy ||
    activeWork?.flags.driveVideoBusy ||
    (autoBusy && stepTargets.video)
  );
  const imagesBusy =
    !!activeWork?.flags.imagesBusy || (autoBusy && stepTargets.images);
  const docsBusy =
    chips.gameKb.status === 'running' || (autoBusy && stepTargets.docs);
  const sourceBusy =
    !!activeWork?.flags.pullSourceBusy || (autoBusy && stepTargets.source);

  const anyLiveBusy =
    autoBusy ||
    texecBusy ||
    videoBusy ||
    imagesBusy ||
    docsBusy ||
    sourceBusy;

  const texecActivity = activityFromStatus(chips.texec.status, texecBusy);
  const videoActivity = activityFromStatus(chips.video.status, videoBusy);
  const imagesActivity = activityFromStatus(
    chips.artifacts.images === 'running'
      ? 'running'
      : chips.artifacts.images === 'ready' || chips.artifacts.images === 'present'
        ? 'done'
        : chips.artifacts.images === 'partial'
          ? 'partial'
          : 'idle',
    imagesBusy || sourceBusy,
  );
  const docsActivity = activityFromStatus(chips.gameKb.status, docsBusy);
  const hasUpdates = boardHasUpdatesSinceVisit(card.runId, {
    texecDone: chips.texec.done,
    videoAnalyzed: chips.video.analyzed,
    docsReady: chips.gameKb.status === 'ready',
    bugsInReview: card.bugs?.inReview ?? 0,
  });

  const imagesLabel =
    chips.artifacts.images === 'running' || imagesBusy
      ? 'Images…'
      : chips.artifacts.images === 'ready' || chips.artifacts.images === 'present'
        ? 'Images ready'
        : chips.artifacts.images === 'missing'
          ? 'Images –'
          : `Images ${chips.artifacts.images}`;

  return (
    <article
      className={`kanban-card${started ? ' kanban-card-started' : ''}${hasUpdates ? ' kanban-card-updates' : ''}${anyLiveBusy ? ' kanban-card-busy' : ''}`}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <div className="row" style={{ gap: 8, alignItems: 'baseline' }}>
        <span className="kanban-sr">{card.srKey}</span>
        <span className="muted small">{card.studioName}</span>
        <span className="spacer" />
        {anyLiveBusy && activeWork?.initials && (
          <span
            className="kanban-worker"
            title={
              activeWork.workerName
                ? `${activeWork.workerName} — Sidekick work in progress`
                : 'Sidekick work in progress'
            }
          >
            <StatusIcon status="in_progress" size={12} />
            <span>{activeWork.initials}</span>
          </span>
        )}
        {hasUpdates && (
          <span
            className="kanban-notif"
            title="Progress completed since you last left this run"
            aria-label="Updates since last visit"
          >
            <svg
              className="kanban-notif-bell"
              viewBox="0 0 24 24"
              width="16"
              height="16"
              aria-hidden
            >
              <path
                fill="currentColor"
                d="M12 22a2.2 2.2 0 0 0 2.2-2.2h-4.4A2.2 2.2 0 0 0 12 22Zm8-5.5h-1.6V10a6.4 6.4 0 0 0-5-6.25V2.8a1.2 1.2 0 1 0-2.4 0v.95A6.4 6.4 0 0 0 5.6 10v6.5H4a1 1 0 1 0 0 2h16a1 1 0 1 0 0-2Z"
              />
            </svg>
            <span className="kanban-notif-badge" />
          </span>
        )}
        {started ? (
          <span className="badge good">Started</span>
        ) : (
          <span className="badge">Not started</span>
        )}
      </div>
      <div className="kanban-summary">{card.srSummary}</div>
      <div className="muted small">
        {(() => {
          const testers = (card.texecAssignees ?? []).filter(Boolean);
          const primary = testers.length > 0 ? testers.join(', ') : 'Unassigned';
          const srName = card.assignee?.trim() || null;
          const showSr =
            !!srName && !testers.some((t) => t.toLowerCase() === srName.toLowerCase());
          return (
            <>
              {primary}
              {card.created ? ` · ${formatAppDate(card.created)}` : ''}
              {showSr ? <div className="muted small">SR: {srName}</div> : null}
            </>
          );
        })()}
      </div>
      <div className="kanban-chips">
        <Chip
          label={`TEXEC ${chips.texec.done}/${chips.texec.total || '–'}`}
          activity={texecActivity}
        />
        <Chip label={imagesLabel} activity={imagesActivity} />
        <Chip
          label={`Video ${chips.video.analyzed}/${chips.video.total || '–'}`}
          activity={videoActivity}
        />
        <Chip
          label={`Docs ${chips.gameKb.status === 'idle' ? '–' : chips.gameKb.status}`}
          activity={docsActivity}
        />
        <Chip
          label={`Bugs in review: ${card.bugs?.inReview ?? 0}`}
          activity={(card.bugs?.inReview ?? 0) > 0 ? 'paused' : null}
          tone={(card.bugs?.inReview ?? 0) > 0 ? 'warn' : ''}
        />
        {(card.bugs?.todo ?? 0) > 0 && (
          <Chip label={`Bugs todo: ${card.bugs!.todo}`} tone="info" />
        )}
        {chips.artifacts.files === 'pruned' && <Chip label="code pruned" tone="warn" />}
      </div>
      {card.runs.length > 1 && (
        <div className="muted small" onClick={(e) => e.stopPropagation()}>
          {card.runs.map((r) => (
            <div key={r.id}>
              <Link to={`/runs/${r.id}`}>{r.texecKey}</Link> — {r.gameName}
            </div>
          ))}
        </div>
      )}
    </article>
  );
}

function StartSrModal({ srKey, onClose }: { srKey: string; onClose: () => void }) {
  const navigate = useNavigate();
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="row" style={{ marginBottom: 12 }}>
          <h2 style={{ margin: 0 }}>Start {srKey}</h2>
          <span className="spacer" />
          <button className="btn btn-ghost" type="button" onClick={onClose}>
            Close
          </button>
        </div>
        <p className="muted small" style={{ marginTop: 0 }}>
          Pick a TEXEC to create a local run. Progress chips on the board will fill in as you work.
        </p>
        <TexecPicker
          srKey={srKey}
          onCreated={(runId) => {
            onClose();
            navigate(`/runs/${runId}/phase/1`);
          }}
          onCancel={onClose}
        />
      </div>
    </div>
  );
}

export function KanbanDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mineOnly, setMineOnly] = useState(false);
  const [startedOnly, setStartedOnly] = useState(false);
  const [assignee, setAssignee] = useState('');
  const [loading, setLoading] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [startSrKey, setStartSrKey] = useState<string | null>(null);
  const activeRuns = useActiveWorkStore((s) => s.runs);
  const fetchActiveWork = useActiveWorkStore((s) => s.fetch);

  const activeByRunId = useMemo(() => {
    const map = new Map<string, ActiveWorkRun>();
    for (const r of activeRuns) map.set(r.runId, r);
    return map;
  }, [activeRuns]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      if (mineOnly) qs.set('mine', '1');
      if (assignee) qs.set('assignee', assignee);
      const q = qs.toString();
      const d = await api.get<DashboardData>(`/api/dashboard${q ? `?${q}` : ''}`);
      setData(d);
    } catch (e: unknown) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [mineOnly, assignee]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    void fetchActiveWork();
  }, [fetchActiveWork]);

  useEffect(() => {
    if (params.get('open') === 'custom') {
      setCustomOpen(true);
      const next = new URLSearchParams(params);
      next.delete('open');
      setParams(next, { replace: true });
    }
  }, [params, setParams]);

  // Debounce board reloads on phase / auto-workflow terminal events so we
  // do not stampede Jira. Live spinners come from /api/dashboard/activity.
  const phaseRefreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (phaseRefreshTimer.current) clearTimeout(phaseRefreshTimer.current);
    };
  }, []);

  const scheduleBoardRefresh = () => {
    if (loading) return;
    if (phaseRefreshTimer.current) clearTimeout(phaseRefreshTimer.current);
    phaseRefreshTimer.current = setTimeout(() => {
      phaseRefreshTimer.current = null;
      void refresh();
    }, 2000);
  };

  useWebSocket(null, (msg) => {
    if (msg.type === 'run-deleted') {
      if (loading) return;
      void refresh();
      void fetchActiveWork();
      return;
    }
    if (msg.type === 'phase') {
      scheduleBoardRefresh();
      void fetchActiveWork();
      return;
    }
    if (msg.type === 'active-work') {
      void fetchActiveWork();
      return;
    }
    if (msg.type === 'auto-workflow-progress') {
      void fetchActiveWork();
      if (
        msg.status === 'completed' ||
        msg.status === 'skipped' ||
        msg.status === 'failed' ||
        msg.step === 'idle' ||
        msg.step === 'done'
      ) {
        scheduleBoardRefresh();
      }
    }
  });

  const filteredCards = useMemo(() => {
    let cards = data?.cards ?? [];
    if (startedOnly) cards = cards.filter((c) => !!c.runId);
    return cards;
  }, [data, startedOnly]);

  const byColumn = useMemo(() => {
    const map = new Map<string, DashCard[]>();
    for (const card of filteredCards) {
      const list = map.get(card.status) ?? [];
      list.push(card);
      map.set(card.status, list);
    }
    const ordered: string[] = [];
    for (const col of data?.columns ?? []) {
      ordered.push(col);
    }
    for (const col of map.keys()) {
      if (!ordered.includes(col)) ordered.push(col);
    }
    return { map, ordered };
  }, [filteredCards, data?.columns]);

  const openCard = (card: DashCard) => {
    // Opening clears the "updates" bell by aligning the visit snapshot to now.
    persistCardSnapshot(card);
    if (card.runId) {
      navigate(`/runs/${card.runId}/phase/1`);
      return;
    }
    setStartSrKey(card.srKey);
  };

  const startedCount = (data?.cards ?? []).filter((c) => !!c.runId).length;

  return (
    <div className="kanban-page">
      <div className="row" style={{ alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0 }}>SR Board</h1>
        <span className="muted small">
          {loading
            ? 'Loading…'
            : `${filteredCards.length} shown · ${startedCount} started locally`}
        </span>
        <span className="spacer" />
        <label
          className="row"
          style={{ gap: 6 }}
          title="TEXECs assigned to you in Jira"
        >
          <input
            type="checkbox"
            checked={mineOnly}
            onChange={(e) => setMineOnly(e.target.checked)}
          />
          Only mine
        </label>
        <label className="row" style={{ gap: 6 }}>
          <input
            type="checkbox"
            checked={startedOnly}
            onChange={(e) => setStartedOnly(e.target.checked)}
          />
          Only started
        </label>
        <select value={assignee} onChange={(e) => setAssignee(e.target.value)}>
          <option value="">All TEXEC assignees</option>
          {(data?.assignees ?? []).map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
        <button className="btn btn-primary" type="button" onClick={() => setCustomOpen(true)}>
          Load custom SR
        </button>
        <button className="btn" type="button" onClick={() => void refresh()} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>
      <p className="muted small">
        Signed in as {user?.email}. Only mine = TEXECs assigned to you in Jira.
        Blue spinner = Sidekick work running now (any teammate);
        amber pause = opened / partial but idle. Initials (MM, IG, …) show who is driving the
        job. Closing an Active chip only hides it from your strip — board chips keep updating.
      </p>
      {error && (
        <div className="card" style={{ borderColor: 'var(--bad)' }}>
          {error}
        </div>
      )}

      <div className="kanban-board">
        {byColumn.ordered.map((col) => (
          <div key={col} className="kanban-column">
            <header className="kanban-column-head">
              <strong>{col}</strong>
              <span className="muted small">{byColumn.map.get(col)?.length ?? 0}</span>
            </header>
            <div className="kanban-column-body">
              {(byColumn.map.get(col) ?? []).map((card) => (
                <CardBody
                  key={card.srKey}
                  card={card}
                  activeWork={pickActiveWorkForCard(card, activeByRunId)}
                  onOpen={() => openCard(card)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {customOpen && (
        <CustomSrDialog
          onClose={() => {
            setCustomOpen(false);
            void refresh();
          }}
        />
      )}
      {startSrKey && (
        <StartSrModal
          srKey={startSrKey}
          onClose={() => {
            setStartSrKey(null);
            void refresh();
          }}
        />
      )}
    </div>
  );
}
